/* Copyright (c) Trimble Inc. All rights reserved. */

import { generateOAuthState } from '../utils/urlBuilder.util';
import { PKCEGenerationHelper } from './PKCEGenerator.helper';
import { AuthConfigStore } from '../stores/AuthConfig.store';
import { useTokenStore } from '../stores/Token.store';
import {
  AuthServiceOptions,
  LoginOptions,
  LoginUrlParams,
  type LogoutOptions,
  LogoutUrlParams,
} from '../models/AuthRedirector.model';
import { getEnvironmentEndpoints } from '../env.config';
import { getCurrentDomain, Logger, Persistor } from '@tcp-base/toolkit';
import { externalRedirect } from '@tcp-base/design-system';
import { useUserStore } from '../stores/User.store';
import { AUTH_REDIRECT_PATH_STORAGE_KEY, OAUTH_CALLBACK_PARAMS } from '../config/CONSTANTS';
import { EnvironmentEndpoints } from '../models/AuthConfig.model';

/**
 * Authentication Service Singleton
 *
 * Handles login and logout functionality by getting configuration from auth context/store.
 * Combines clientId, scopes, and environment, gets proper env config, and redirects to URLs.
 *
 * Features:
 * - Singleton pattern for global access
 * - Gets configuration from AuthConfigStore (no props needed)
 * - Builds OAuth2 URLs based on environment
 * - Handles login and logout redirects
 * - State management for OAuth2 security
 */
class AuthRedrectionHelperImpl {
  private isLoginInProgress = false; // Prevent concurrent login calls
  private options: AuthServiceOptions = {
    defaultRedirectUri: getCurrentDomain(),
    defaultPostLogoutRedirectUri: getCurrentDomain(),
    useCodeChallenge: true,
  };

  /**
   * Configure the auth service
   */
  public configure(options: AuthServiceOptions): void {
    this.options = { ...this.options, ...options };
  }

  /**
   * Check if authentication is properly configured
   */
  public isConfigured(): boolean {
    return AuthConfigStore.isConfigured();
  }

  /**
   * Login - Redirect to OAuth2 authorization endpoint with PKCE
   * Establishes initial PKCE chain for secure OAuth2 flow
   */
  public async login(options: LoginOptions = {}): Promise<void> {
    // Prevent concurrent login attempts
    if (this.isLoginInProgress) {
      // Login already in progress, ignoring duplicate call
      return;
    }

    try {
      this.isLoginInProgress = true;
      // Starting login process

      // Reset tab-scoped state from any prior session before starting a new login.
      // Team switching saves the selected group first and must keep that selection.
      if (!options.preserveIamStore) {
        try {
          Persistor.removeItem('iam-store');
        } catch (storageError) {
          Logger.warn('Failed to clear stale session state before login:', storageError);
        }
      }

      // Generate state for security
      const state = options.state || generateOAuthState();

      // Store state for validation on return
      try {
        Persistor.setItem('oauth_state', state);
      } catch (storageError) {
        Logger.warn('Failed to store OAuth state:', storageError);
        // Continue with login even if storage fails
      }

      // Save current path (pathname + search + hash) for redirect after login.
      // Strip any OAuth callback params first — the redirect_uri can be the
      // current page so the callback may land here with ?code= in the URL.
      try {
        const cleanUrl = new URL(window.location.href);
        OAUTH_CALLBACK_PARAMS.forEach((p) => cleanUrl.searchParams.delete(p));
        const currentPath = cleanUrl.pathname + cleanUrl.search + cleanUrl.hash;
        // Only save if it's not the root path or login-related path
        if (currentPath && currentPath !== '/' && !currentPath.includes('/callback')) {
          Persistor.setItem(AUTH_REDIRECT_PATH_STORAGE_KEY, currentPath);
          Logger.debug('AuthService: Saved redirect path:', currentPath);
        }
      } catch (pathStorageError) {
        Logger.warn('Failed to store redirect path:', pathStorageError);
        // Continue with login even if path storage fails
      }

      // Get login URL with PKCE (this establishes the initial chain)
      const loginUrl = await this.getLoginUrl({
        ...options,
        state, // Use the generated state
      });

      // Redirect to login
      externalRedirect(loginUrl, '_self');

      // Note: isLoginInProgress is intentionally not reset on success
      // since the page will navigate away
    } catch (error) {
      Logger.error('AuthService: Login failed:', error);
      this.isLoginInProgress = false; // Reset on error
      throw error;
    }
  }

  /**
   * Logout - Clear tokens and redirect to logout endpoint
   */
  // @LogErrors()
  public logout(options: LogoutOptions = {}): void {
    try {
      const params = AuthConfigStore.getParams();
      const id_token = useTokenStore.getState()?.tokenDetails?.id_token;
      // Clear local storage if requested (default: true)
      if (options.clearLocalStorage !== false) {
        useTokenStore.getState()?.removeTokenDetails();
        useUserStore.getState()?.removeUserData();

        // Clear auth and IAM session data to prevent stale data on re-login
        try {
          Persistor.removeItem('oauth_state');
          Persistor.removeItem('iam-store');
        } catch (storageError) {
          Logger.warn('Failed to clear session storage:', storageError);
        }
        // clearing Local storage on logout to remove any persisted data related to auth, user, or tokens. This ensures a clean state for the next login.
        try {
          localStorage.clear();
        } catch (storageError) {
          Logger.warn('Failed to clear local storage:', storageError);
        }
      }

      // If no environment configured, just clear and return
      if (!params.environment) {
        Logger.warn('AuthService: No environment configured for logout redirect');
        return;
      }

      // Generate state for logout
      const state = options.state || generateOAuthState();

      // Build logout URL
      const logoutUrl = this.buildLogoutUrl({
        environment: params.environment,
        postLogoutRedirectUri:
          options.postLogoutRedirectUri || (this.options.defaultPostLogoutRedirectUri as string),
        clientId: params.clientId,
        state,
        idTokenHint: id_token || '',
      });

      // Redirect to logout
      externalRedirect(logoutUrl, '_self');
    } catch (error) {
      Logger.error('AuthService: Logout failed:', error);
      throw error;
    }
  }

  /**
   * Build OAuth2 authorization URL for login
   */
  private buildLoginUrl(params: LoginUrlParams): string {
    const {
      clientId,
      scopes,
      environment,
      redirectUri,
      state,
      codeChallenge,
      codeChallengeMethod,
      accountId,
      context,
      prompt,
      loginHint,
    } = params;

    const endpoints = getEnvironmentEndpoints(environment);
    const authUrl = new URL(endpoints.AUTHORIZATION_ENDPOINT);

    // Required parameters
    authUrl.searchParams.set('client_id', clientId);
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('scope', scopes.join(' '));
    authUrl.searchParams.set('redirect_uri', redirectUri);

    // Optional parameters
    authUrl.searchParams.set('state', state);

    authUrl.searchParams.set('code_challenge', codeChallenge);
    authUrl.searchParams.set('code_challenge_method', codeChallengeMethod);

    if (accountId) {
      authUrl.searchParams.set('account_id', accountId);
    }

    if (context) {
      authUrl.searchParams.set('context', context);
    }

    // Optional OIDC parameters for specialized flows (e.g. secret re-auth firewall)
    if (prompt) {
      authUrl.searchParams.set('prompt', prompt);
    }
    if (loginHint) {
      authUrl.searchParams.set('login_hint', loginHint);
    }

    return authUrl.toString();
  }

  /**
   * Build logout URL
   */
  private buildLogoutUrl(params: LogoutUrlParams): string {
    const { environment, postLogoutRedirectUri, clientId, state, idTokenHint } = params;

    const endpoints = getEnvironmentEndpoints(environment);
    const logoutUrl = new URL(endpoints.END_SESSION_ENDPOINT);

    if (idTokenHint) {
      logoutUrl.searchParams.set('id_token_hint', idTokenHint);
    }

    logoutUrl.searchParams.set('post_logout_redirect_uri', postLogoutRedirectUri);
    logoutUrl.searchParams.set('state', state);

    // Optional parameters
    if (clientId) {
      logoutUrl.searchParams.set('client_id', clientId);
    }

    return logoutUrl.toString();
  }

  /**
   * Get login URL without redirecting (useful for programmatic access)
   * Generates PKCE codes to establish initial chain for secure OAuth2 flow
   */
  public async getLoginUrl(options: LoginOptions = {}): Promise<string> {
    const params = AuthConfigStore.getParams();

    if (!params.clientId || !params.environment || !params.scopes) {
      throw new Error('AuthService: Missing required configuration parameters');
    }

    const allScopes = options.additionalScopes
      ? [...params.scopes, ...options.additionalScopes]
      : [...params.scopes];

    // Generate PKCE codes for secure OAuth2 flow
    const pkceResult = await PKCEGenerationHelper.generate();
    return this.buildLoginUrl({
      clientId: params.clientId,
      scopes: allScopes,
      environment: params.environment,
      redirectUri: options.redirectUri || (this.options.defaultRedirectUri as string),
      state: options.state || generateOAuthState(),
      codeChallenge: pkceResult.newCodeChallenge,
      codeChallengeMethod: pkceResult.codeChallengeMethod,
      accountId: options.accountId,
      context: options.context,
      prompt: options.prompt,
      loginHint: options.loginHint,
    });
  }

  /**
   * Get logout URL without redirecting (useful for programmatic access)
   */
  public getLogoutUrl(options: LogoutOptions = {}): string {
    const params = AuthConfigStore.getParams();

    if (!params.environment) {
      throw new Error('AuthService: Environment not configured');
    }

    return this.buildLogoutUrl({
      environment: params.environment,
      postLogoutRedirectUri:
        options.postLogoutRedirectUri || (this.options.defaultPostLogoutRedirectUri as string),
      clientId: params.clientId,
      state: options.state || generateOAuthState(),
      idTokenHint: '',
    });
  }

  /**
   * Get current environment endpoints
   */
  public getEndpoints(): EnvironmentEndpoints | null {
    return AuthConfigStore.getEndpoints();
  }
}

// Export singleton instance
export const AuthRedrectionHelper = new AuthRedrectionHelperImpl();

// Correctly spelled alias for new call sites; keep typo'd export for compatibility.
export const AuthRedirectionHelper = AuthRedrectionHelper;

// Export class for type checking
export default AuthRedrectionHelper;
